import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { CollabManager } from '@databases/collab-state-manager/collab-manager';
import { getVersionId } from '@databases/database-state';
import { currentBridgeData } from '@databases/database-state/state/bridge-data';
import { perf } from '@databases/logs/performance';
import { presenceState } from '@databases/presence';
import { createLoader } from '@databases/sdk-loader';
import { type ConnectedDatabase } from '@databases/sdk-loader/connected-database';
import { container } from '@databases/service-container';
import { captureException } from '@sentry/core';
import { createIntl, createIntlCache } from 'react-intl-next';

import {
  featureFlagsInitialized,
  getExperimentValue,
} from '@canvas/client-feature-flags';
import { transformToken } from '@canvas/frame-api/canvas-frame-api';

/**
 * Fires the SDK loader to begin loading the database over a live Yjs collab
 * connection. This runs BEFORE bootstrap so the WebSocket handshake and document
 * sync happen in parallel with intl fetching.
 *
 * Returns a promise that resolves to the connected database, or `null` if the
 * preconditions aren't met (no contentReferenceId or no valid token).
 *
 * The caller must await this promise AFTER bootstrap completes, then call
 * {@link resolveSdkDatabaseLoad} to inject the result into Redux.
 *
 * Ordering safety:
 * - The WebSocket connection is gated on `featureFlagsInitialized` because
 *   {@link getCanvasServerUrl} depends on `databases-collab-use-canvas-tenant-context-route`.
 *   Without this gate we would silently use the legacy URL when the flag is on.
 * - A fallback English `IntlShape` is registered synchronously so migrations
 *   in `CollabManager.initialLoad` don't crash if they resolve `Intl` before
 *   bootstrap has registered the real one.
 * - `versionId` and `presenceState` are plumbed into `CollabManager` to match
 *   the legacy `createCollabManager` behavior (versioned viewing, telepointers).
 */
export function trySdkDatabaseLoadFromCurrentBridge(): Promise<ConnectedDatabase | null> | null {
  const { activationId, cloudId, contentId, contentReferenceId, spaceId, token } =
    currentBridgeData.data;

  if (!contentReferenceId) {
    return null;
  }

  const frameToken = transformToken(token);
  if (!frameToken) {
    return null;
  }

  try {
    const fallbackIntl = createIntl({ locale: 'en', messages: {} }, createIntlCache());
    container.registerSingleton('Intl', () => fallbackIntl);

    perf.timeLog('app-initialization', 'sdk-database-load-start');
    GlobalPageLoadExperience.mark('sdk-database-load-start');

    const versionId = getVersionId();

    const loader = createLoader({
      createCollabManager: (
        loaderCloudId,
        loaderActivationId,
        loaderSpaceId,
        loaderContentId,
        loaderReferenceId,
        ydb,
        loaderToken,
      ) =>
        new CollabManager(
          loaderCloudId,
          loaderActivationId,
          loaderSpaceId,
          loaderContentId,
          loaderReferenceId,
          ydb,
          loaderToken,
          versionId,
          presenceState,
        ),
    });

    // We wait for flags before connecting because `getCanvasServerUrl` reads
    // `databases-collab-use-canvas-tenant-context-route` to choose the collab
    // endpoint. Future improvement: pass the URL explicitly so we can fire
    // the WebSocket ~200ms earlier (before flags resolve), eliminating this gate.
    return featureFlagsInitialized.then(() => {
      const isEnabled = getExperimentValue('databasesSdkLoaderBypass', 'isEnabled', {
        fireExperimentExposure: false,
      });

      if (!isEnabled) {
        return null;
      }

      return loader({
        cloudId,
        activationId,
        spaceId,
        contentId,
        referenceId: contentReferenceId,
        token: frameToken,
      });
    });
  } catch (error) {
    captureException(error);
    return null;
  }
}
